import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { BuildingPlacement, FormKeys } from '@nx-customer-apps/shared/enums';
import { BuildingType } from '@nx-customer-apps/components';
import { FormOptions } from '@nx-customer-apps/shared/interfaces';
import { CheckboxQuestion, CheckboxSet, MultiTextbox, RadioQuestion, TextboxQuestion } from '@nx-customer-apps/shared/utils';
import { FormToolsService, ValidatorsService } from '../../../../services';

@Injectable({
    providedIn: 'root'
})
export class BuildingHullFormService {
    private formToolsService = inject(FormToolsService);
    private validatorsService = inject(ValidatorsService);

    public getBuildingQuestions(options: FormOptions): (RadioQuestion | MultiTextbox | CheckboxSet)[] {
        const buildingDimensions = this.buildingDimensions(options);
        const buildingPosition = this.getBuildingPosition(options);
        const ventilationAndHeating = this.getVentilationAndHeating(options);

        return [buildingDimensions, buildingPosition, ventilationAndHeating];
    }

    private getBuildingPosition(options: FormOptions): RadioQuestion {
        return new RadioQuestion({
            question: 'HEATLOAD.BUILDING.POSITION.TITLE',
            description: 'HEATLOAD.BUILDING.POSITION.DESCRIPTION',
            defaultErrorMessage: 'HEATLOAD.BUILDING.POSITION.ERRORS.REQUIRED',
            key: FormKeys.BuildingPosition,
            validators: [Validators.required],
            styles: 'ca-radio-columns',
            options: [
                { key: 'HEATLOAD.BUILDING.POSITION.FREE_STANDING', value: BuildingPlacement.Detached },
                { key: 'HEATLOAD.BUILDING.POSITION.MIDDLE_HOUSE', value: BuildingPlacement.Middle },
                { key: 'HEATLOAD.BUILDING.POSITION.CORNER_HOUSE', value: BuildingPlacement.Corner }
            ],
            value: options?.questions?.[FormKeys.BuildingPosition]?.value
        });
    }

    private getVentilationAndHeating(options: FormOptions): CheckboxSet {
        return new CheckboxSet({
            question: 'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.TITLE',
            descriptionList: [
                'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.FIRST_BULLET',
                'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.SECOND_BULLET'
            ],
            groupName: FormKeys.VentilationAndHeating,
            styles: 'flex flex-col ca-ventilation-and-heating',
            questions: [
                new CheckboxQuestion({
                    key: FormKeys.VentilationPresent,
                    label: 'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.VENTILATION_PRESENT',
                    value: options?.questions?.[FormKeys.VentilationPresent]?.value
                }),
                new CheckboxQuestion({
                    key: FormKeys.AtticPresent,
                    label: 'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.ATTIC_PRESENT',
                    value: options?.questions?.[FormKeys.AtticPresent]?.value
                }),
                new CheckboxQuestion({
                    key: FormKeys.AtticHeated,
                    label: 'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.ATTIC_HEATED',
                    disabled: !options?.questions?.[FormKeys.AtticPresent]?.value,
                    value: options?.questions?.[FormKeys.AtticHeated]?.value
                }),
                new CheckboxQuestion({
                    key: FormKeys.BasementPresent,
                    label: 'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.BASEMENT_PRESENT',
                    value: options?.questions?.[FormKeys.BasementPresent]?.value
                }),
                new CheckboxQuestion({
                    key: FormKeys.BasementHeated,
                    label: 'HEATLOAD.BUILDING.VENTILATION_AND_HEATING.BASEMENT_HEATED',
                    disabled: !options?.questions?.[FormKeys.BasementPresent]?.value,
                    value: options?.questions?.[FormKeys.BasementHeated]?.value
                })
            ]
        });
    }
    private buildingDimensions(options: FormOptions): MultiTextbox {
        return new MultiTextbox({
            question: 'HEATLOAD.BUILDING.DIMENSIONS.TITLE',
            bottomDescription: 'HEATLOAD.BUILDING.DIMENSIONS.NOTE',
            descriptionList: [
                'HEATLOAD.BUILDING.DIMENSIONS.FIRST_BULLET',
                'HEATLOAD.BUILDING.DIMENSIONS.SECOND_BULLET',
                'HEATLOAD.BUILDING.DIMENSIONS.THIRD_BULLET'
            ],
            groupName: FormKeys.BuildingDimensions,
            styles: 'ca-form-field-columns',
            questions: [
                this.buildingLength(options),
                this.buildingWidth(options),
                this.buildingFloorHeight(options),
                this.buildingFloorsNumber(options)
            ]
        });
    }

    private buildingLength(options: FormOptions): TextboxQuestion {
        if (!options.buildingType) {
            throw Error('Please specify buildingType for buildingLength question.');
        }

        const settings = {
            [BuildingType.SingleFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 2, max: 50 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' })
            },
            [BuildingType.MultiFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 10, max: 999 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '100', numberOfDecimals: '2' })
            }
        };

        const selectedSettings = settings[options.buildingType];

        return new TextboxQuestion({
            label: 'HEATLOAD.BUILDING.DIMENSIONS.BUILDING_LENGTH.LABEL',
            defaultErrorMessage: 'HEATLOAD.BUILDING.DIMENSIONS.BUILDING_LENGTH.ERRORS.REQUIRED',
            key: FormKeys.BuildingLength,
            validators: [Validators.required, selectedSettings.minMaxValidator],
            mask: selectedSettings.mask,
            suffix: 'MEASUREMENT_UNITS.M',
            styles: 'with-wrapped-error-message',
            value: options?.questions?.[FormKeys.BuildingLength]?.value || ''
        });
    }

    private buildingWidth(options: FormOptions): TextboxQuestion {
        if (!options.buildingType) {
            throw Error('Please specify buildingType for buildingWidth question.');
        }

        const settings = {
            [BuildingType.SingleFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 2, max: 50 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' })
            },
            [BuildingType.MultiFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 10, max: 999 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '100', numberOfDecimals: '2' })
            }
        };

        const selectedSettings = settings[options.buildingType];

        return new TextboxQuestion({
            label: 'HEATLOAD.BUILDING.DIMENSIONS.BUILDING_WIDTH.LABEL',
            defaultErrorMessage: 'HEATLOAD.BUILDING.DIMENSIONS.BUILDING_WIDTH.ERRORS.REQUIRED',
            key: FormKeys.BuildingWidth,
            validators: [Validators.required, selectedSettings.minMaxValidator],
            mask: selectedSettings.mask,
            suffix: 'MEASUREMENT_UNITS.M',
            styles: 'with-wrapped-error-message',
            value: options?.questions?.[FormKeys.BuildingWidth]?.value || ''
        });
    }

    private buildingFloorHeight(options: FormOptions): TextboxQuestion {
        if (!options.buildingType) {
            throw Error('Please specify buildingType for buildingFloorHeight question.');
        }

        const settings = {
            [BuildingType.SingleFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 1.5, max: 15 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '10', numberOfDecimals: '2' })
            },
            [BuildingType.MultiFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 1.5, max: 5 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '1', numberOfDecimals: '2' })
            }
        };

        const selectedSettings = settings[options.buildingType];

        return new TextboxQuestion({
            label: 'HEATLOAD.BUILDING.DIMENSIONS.FLOOR_HEIGHT.LABEL',
            key: FormKeys.FloorHeight,
            defaultErrorMessage: 'HEATLOAD.BUILDING.DIMENSIONS.FLOOR_HEIGHT.ERRORS.REQUIRED',
            validators: [Validators.required, selectedSettings.minMaxValidator],
            mask: selectedSettings.mask,
            suffix: 'MEASUREMENT_UNITS.M',
            styles: 'with-wrapped-error-message',
            value: options?.questions?.[FormKeys.FloorHeight]?.value || '2.55'
        });
    }

    private buildingFloorsNumber(options: FormOptions): TextboxQuestion {
        if (!options.buildingType) {
            throw Error('Please specify buildingType for buildingFloorsNumber question.');
        }

        const settings = {
            [BuildingType.SingleFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 1, max: 5 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '1' })
            },
            [BuildingType.MultiFamilyHouse]: {
                minMaxValidator: this.validatorsService.minMaxValidator({ min: 1, max: 50 }),
                mask: this.formToolsService.getNumericMask({ separatorLimit: '10' })
            }
        };

        const selectedSettings = settings[options.buildingType];

        return new TextboxQuestion({
            label: 'HEATLOAD.BUILDING.DIMENSIONS.FLOORS_NUMBER.LABEL',
            key: FormKeys.NumberOfFloors,
            defaultErrorMessage: 'HEATLOAD.BUILDING.DIMENSIONS.FLOORS_NUMBER.ERRORS.REQUIRED',
            validators: [Validators.required, selectedSettings.minMaxValidator],
            mask: selectedSettings.mask,
            styles: 'with-wrapped-error-message',
            value: options?.questions?.[FormKeys.NumberOfFloors]?.value || ''
        });
    }
}
